/* La Sura - links that carry cake builder choices (loop 4, DECISIONS
   2026-09-27).

   Reading: a builder URL like ?Size=8+inch&Dietary=Vegan|Made+without+nuts
   picks those choices when the page opens. Keys are the option names
   (Size, Layers) and the builder groups' headings (Dietary, Sponge...),
   matched without caring about case; several values join with "|". Size and
   height are set one at a time, waiting for Horizon to swap the variant
   picker in between. A value that isn't offered is simply skipped.

   Writing: any link with data-carry-choices (the switch between Step by
   step and the scroll page) gets the current choices added to it when it's
   followed. A link with data-carry-enquiry (the builder's Start an enquiry
   button, loop 5) gets them in the bespoke enquiry form's terms instead:
   servings, flavours, allergens and theme (assets/lasura-enquiry-wizard.js
   fills its fields from those). Only option choices travel. The message,
   photos, phone number and follow-up answers (a name on a topper) never go
   into a URL. "Add one of our toppers?" (data-our-toppers, loop 7 part 2)
   has its own keys, Our topper, Our topper style and Candles, the app's
   too: its controller (lasura-cake-our-toppers.js) writes and reads them. */
(() => {
  if (window.lasuraCakePrefill) return;
  window.lasuraCakePrefill = true;

  const legend = (fieldset) => fieldset.querySelector('legend')?.childNodes[0]?.textContent.trim() || '';
  const lower = (text) => String(text || '').trim().toLowerCase();
  /* The dietary group's heading is a question, so its key is its order name. */
  const key = (group) => (group.hasAttribute('data-builder-dietary') ? group.dataset.property || 'Dietary' : group.dataset.groupLabel);

  const currentChoices = () => {
    const params = new URLSearchParams();
    document.querySelectorAll('variant-picker fieldset').forEach((fieldset) => {
      const value = fieldset.querySelector('input:checked')?.value;
      if (legend(fieldset) && value) params.set(legend(fieldset), value);
    });
    document.querySelectorAll('lasura-cake-builder [data-builder-group][data-group-label]').forEach((group) => {
      if (group.querySelector('[data-builder-phone]') || group.hasAttribute('data-our-toppers')) return;
      const values = [...group.querySelectorAll('input:checked')]
        .filter((input) => input.value && !input.closest('[hidden]'))
        .map((input) => input.value);
      if (values.length) params.set(key(group), values.join('|'));
    });
    (window.LasuraOurToppers?.pairs() || []).forEach(([k, v]) => params.set(k, v));
    return params;
  };

  /* Sponge and filling are flavours, dietary needs are allergens, the rest
     (size, height, colour, toppings...) describe the look. */
  const enquiryChoices = () => {
    const look = [];
    const flavours = [];
    const params = new URLSearchParams({ from: 'builder' });
    const serves = [...document.querySelectorAll('variant-picker input:checked')]
      .map((input) => input.closest('label')?.querySelector('[data-serves-max]'))
      .find(Boolean);
    if (serves) params.set('servings', serves.dataset.servesMax);
    document.querySelectorAll('variant-picker fieldset').forEach((fieldset) => {
      const value = fieldset.querySelector('input:checked')?.value;
      if (legend(fieldset) && value) look.push(`${legend(fieldset)}: ${value}`);
    });
    document.querySelectorAll('lasura-cake-builder [data-builder-group][data-group-label]').forEach((group) => {
      if (group.querySelector('[data-builder-phone]')) return;
      const values = [...group.querySelectorAll('input:checked')]
        .filter((input) => input.value && !input.closest('[hidden]'))
        .map((input) => input.value);
      if (!values.length) return;
      if (group.hasAttribute('data-builder-dietary')) return params.set('allergens', values.join(', '));
      const line = `${group.dataset.groupLabel}: ${values.join(', ')}`;
      (/^cake_(sponge|filling)$/.test(group.dataset.source) ? flavours : look).push(line);
    });
    if (flavours.length) params.set('flavours', flavours.join('; '));
    if (look.length) params.set('theme', look.join('; '));
    return params;
  };

  const once = (type, ms) =>
    new Promise((resolve) => {
      const done = () => {
        document.removeEventListener(type, done);
        resolve();
      };
      document.addEventListener(type, done);
      setTimeout(done, ms);
    });

  /* Also used by assets/lasura-cake-edit.js, which passes the choices read back from a basket
     line instead of the URL. */
  const apply = async (source) => {
    const params = source instanceof URLSearchParams ? source : new URLSearchParams(location.search);
    const wanted = new Map([...params].map(([key, value]) => [lower(key), value.split('|').map(lower)]));
    if (!wanted.size) return;

    const names = [...document.querySelectorAll('variant-picker fieldset')].map(legend);
    for (const name of names) {
      const want = wanted.get(lower(name));
      if (!want) continue;
      const fieldset = [...document.querySelectorAll('variant-picker fieldset')].find((f) => legend(f) === name);
      const input = fieldset && [...fieldset.querySelectorAll('input')].find((i) => lower(i.value) === want[0]);
      if (input && !input.checked) {
        input.click();
        await once('variant:update', 3000);
      }
    }

    document.querySelectorAll('lasura-cake-builder [data-builder-group][data-group-label]').forEach((group) => {
      const want = !group.hasAttribute('data-our-toppers') && wanted.get(lower(key(group)));
      if (!want) return;
      group.querySelectorAll('input').forEach((input) => {
        if (want.includes(lower(input.value)) && !input.checked && !input.disabled && !input.closest('[hidden]')) input.click();
      });
    });
    /* After the size, so a topper too wide for it is taken off with a note. */
    await window.LasuraOurToppers?.apply(params);
  };

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest && event.target.closest('a[data-carry-choices], a[data-carry-enquiry]');
      if (!link) return;
      const url = new URL(link.getAttribute('href'), location.href);
      const choices = link.hasAttribute('data-carry-enquiry') ? enquiryChoices() : currentChoices();
      choices.forEach((value, key) => url.searchParams.set(key, value));
      link.href = url.toString();
    },
    true
  );

  window.lasuraCakePrefillApply = apply;
  /* The current choices as URL parameters, for lasura-cake-share.js (Send this cake, Carry on). */
  window.lasuraCakeChoices = currentChoices;

  /* Opened from the basket's "Change this cake" (?edit=<line key>): load the edit script only then,
     so the builder page doesn't carry it on every visit. */
  const editStrings = document.querySelector('.lasura-cake-edit-strings');
  if (editStrings?.dataset.script && new URLSearchParams(location.search).has('edit')) {
    import(editStrings.dataset.script).catch(() => {});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setTimeout(apply, 0));
  else setTimeout(apply, 0);
})();
